import {
  Stack,
  Group,
  Button,
  Text,
  Divider,
  NumberInput,
  TextInput,
  Textarea,
  Paper,
  Badge,
  ActionIcon,
  Alert,
  BottomDrawer,
  FormField,
  AsyncSearchSelect,
  Select,
  type AsyncSearchPageResult,
} from '@vipso/shared';
import {
  IconPlus,
  IconTrash,
  IconArrowLeft,
  IconArrowRight,
  IconCheck,
  IconShoppingCart,
  IconBuildingWarehouse,
  IconUser,
  IconTruck,
  IconCoins,
} from '@tabler/icons-react';
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useLazyListCustomersQuery,
  useLazyListVendorsQuery,
  useLazyListProductsQuery,
  useAddUpdateProcurementMutation,
  useListChambersQuery,
  useLazyListFloorsQuery,
  useLazyListRacksQuery,
  useGetProductStockAtRackQuery,
} from '../../api/tenantApi';
import {
  useLazyListWarehousesQuery,
  useLazyListLabourQuery,
  useLazyListTransportQuery,
} from '../../api/enhanceGenerated';

// ─── Types ───────────────────────────────────────────────────────────────────

type ProcType = 'SALES' | 'PURCHASE';
type PartyType = 'customer' | 'vendor';

interface ProductSubLine {
  subType: 'A' | 'B' | 'C';
  qtyBase: number;
  quantity: number;
}

interface ProductLine {
  productId: string;
  productName: string;
  quantity: number;
  pricePerUnit: number;
  showSubse: boolean;
  addOnItem: boolean;
  addOnItemName: string;
  addOnItemQty: number;
  qtyUnit: string;
  chamberId: string;
  chamberName: string;
  floorId: string;
  floorName: string;
  rackId: string;
  rackName: string;
  lossPercent: number;
  lossKg: number;
  subs: ProductSubLine[];
}

interface ProductMeta {
  showSubse: boolean;
  addOnItem: boolean;
  addOnItemName: string;
  unit: string;
  pricePerQuantity: number;
}

interface Step1State {
  procType: ProcType;
  partyType: PartyType;
  partyId: string;
  partyName: string;
  warehouseId: string;
  warehouseName: string;
  products: ProductLine[];
}

interface Step2State {
  labourId: string;
  labourName: string;
  transportId: string;
  transportName: string;
  vehicleNo: string;
  receiptNumber: string;
  gatePassNumber: string;
  loadNumber: string;
  bagNumber: string;
  chargesType: string;
  chargesAmount: number;
  chargesDirection: 'add' | 'sub';
  discount: number;
  discountType: 'PERCENT' | 'FLAT';
  tax: number;
  taxType: 'PERCENT' | 'FLAT';
  remarks: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function emptySubs(): ProductSubLine[] {
  return [
    { subType: 'A', qtyBase: 0, quantity: 0 },
    { subType: 'B', qtyBase: 0, quantity: 0 },
    { subType: 'C', qtyBase: 0, quantity: 0 },
  ];
}

function recomputeSubQty(
  sub: ProductSubLine,
  addOnItem: boolean,
  addOnItemQty: number,
): ProductSubLine {
  const base = Number(sub.qtyBase) || 0;
  // Mobile: A uses base as qty; B/C multiply by add-on qty (or 1)
  if (sub.subType === 'A') {
    return { ...sub, quantity: base };
  }
  const mult = addOnItem ? Number(addOnItemQty) || 1 : 1;
  return { ...sub, quantity: base * mult };
}

function lineNetQty(line: ProductLine): number {
  if (!line.showSubse) return Math.max(0, Number(line.quantity) || 0);
  const deduction = line.subs.reduce((s, sub) => s + (Number(sub.quantity) || 0), 0);
  return Math.max(0, (Number(line.quantity) || 0) - deduction);
}

function linePrice(line: ProductLine): number {
  return lineNetQty(line) * (Number(line.pricePerUnit) || 0);
}

function formatCurrency(n: number) {
  return `₹${n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}

function asPickerOptions(rows: unknown): AsyncSearchPageResult['rows'] {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const r = row as Record<string, unknown>;
      const id = r.id ?? r.customerId ?? r.vendorId ?? r.productId;
      const name = r.name ?? r.productName ?? r.customerName ?? r.vendorName;
      if (id == null || name == null) return null;
      return { value: String(id), label: String(name) };
    })
    .filter((r): r is { value: string; label: string } => r != null);
}

function computeTotal(products: ProductLine[], s2: Step2State) {
  const base = products.reduce((sum, p) => sum + linePrice(p), 0);
  const discountVal = s2.discountType === 'PERCENT' ? (base * s2.discount) / 100 : s2.discount;
  const taxVal = s2.taxType === 'PERCENT' ? (base * s2.tax) / 100 : s2.tax;
  const chargesVal = s2.chargesDirection === 'add' ? s2.chargesAmount : -s2.chargesAmount;
  const final = Math.max(0, base - discountVal + taxVal + chargesVal);
  return { base, discountVal, taxVal, chargesVal, final };
}

const emptyLine = (): ProductLine => ({
  productId: '',
  productName: '',
  quantity: 1,
  pricePerUnit: 0,
  showSubse: false,
  addOnItem: false,
  addOnItemName: '',
  addOnItemQty: 1,
  qtyUnit: '',
  chamberId: '',
  chamberName: '',
  floorId: '',
  floorName: '',
  rackId: '',
  rackName: '',
  lossPercent: 0,
  lossKg: 0,
  subs: emptySubs(),
});

const defaultS1 = (procType: ProcType = 'SALES'): Step1State => ({
  procType,
  partyType: 'customer',
  partyId: '',
  partyName: '',
  warehouseId: '',
  warehouseName: '',
  products: [emptyLine()],
});

const defaultS2 = (): Step2State => ({
  labourId: '',
  labourName: '',
  transportId: '',
  transportName: '',
  vehicleNo: '',
  receiptNumber: '',
  gatePassNumber: '',
  loadNumber: '',
  bagNumber: '',
  chargesType: '',
  chargesAmount: 0,
  chargesDirection: 'add',
  discount: 0,
  discountType: 'PERCENT',
  tax: 0,
  taxType: 'PERCENT',
  remarks: '',
});

function composeReceipt(bagNumber: string, loadNumber: string, receiptNumber: string) {
  if (receiptNumber.trim()) return receiptNumber.trim();
  const bag = bagNumber.trim();
  const load = loadNumber.trim();
  if (bag && load) return `${bag}-${load}`;
  return bag || load || '';
}

// ─── Step indicator ──────────────────────────────────────────────────────────

function StepIndicator({ step }: { step: 1 | 2 }) {
  const { t } = useTranslation();
  const steps = [
    { n: 1, label: t('orders.wizard.step1') },
    { n: 2, label: t('orders.wizard.step2') },
  ];
  return (
    <Group gap="xs" justify="center" mb="md">
      {steps.map((s, idx) => {
        const active = s.n === step;
        const done = s.n < step;
        return (
          <Group key={s.n} gap={4} wrap="nowrap">
            <Group gap={6} wrap="nowrap">
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  background:
                    done || active
                      ? 'var(--mantine-color-brand-6)'
                      : 'var(--mantine-color-gray-3)',
                  flexShrink: 0,
                }}
              >
                {done ? (
                  <IconCheck size={13} color="white" />
                ) : (
                  <Text
                    size="xs"
                    fw={700}
                    style={{
                      color: active ? 'white' : 'var(--mantine-color-gray-6)',
                      lineHeight: 1,
                    }}
                  >
                    {s.n}
                  </Text>
                )}
              </div>
              <Text size="xs" fw={active ? 700 : 400} c={active ? 'brand' : 'dimmed'}>
                {s.label}
              </Text>
            </Group>
            {idx < steps.length - 1 && (
              <div
                style={{
                  width: 28,
                  height: 2,
                  background: done
                    ? 'var(--mantine-color-brand-6)'
                    : 'var(--mantine-color-gray-3)',
                  margin: '0 4px',
                  flexShrink: 0,
                }}
              />
            )}
          </Group>
        );
      })}
    </Group>
  );
}

function TypeToggle<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: string }>;
}) {
  return (
    <Group gap={4} wrap="nowrap">
      {options.map((o) => (
        <Button
          key={o.value}
          size="xs"
          variant={value === o.value ? 'filled' : 'default'}
          onClick={() => onChange(o.value)}
          styles={{ root: { flex: 1, fontWeight: value === o.value ? 700 : 400 } }}
        >
          {o.label}
        </Button>
      ))}
    </Group>
  );
}

// ─── Product line ─────────────────────────────────────────────────────────────

interface ProductRowProps {
  line: ProductLine;
  index: number;
  total: number;
  procType: ProcType;
  onUpdate: (idx: number, patch: Partial<ProductLine>) => void;
  onRemove: (idx: number) => void;
  onSelectProduct: (idx: number, id: string, name: string) => void;
  fetchProducts: (args: {
    search: string;
    page: number;
    pageLimit: number;
  }) => Promise<AsyncSearchPageResult>;
  chamberOptions: Array<{ value: string; label: string }>;
  errors?: { productId?: string; quantity?: string };
}

function ProductRow({
  line,
  index,
  total,
  procType,
  onUpdate,
  onRemove,
  onSelectProduct,
  fetchProducts,
  chamberOptions,
  errors,
}: ProductRowProps) {
  const { t } = useTranslation();
  const net = lineNetQty(line);
  const price = linePrice(line);
  const unit = line.qtyUnit ? ` (${line.qtyUnit})` : '';
  const [fetchFloors] = useLazyListFloorsQuery();
  const [fetchRacks] = useLazyListRacksQuery();
  const [floorOptions, setFloorOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [rackOptions, setRackOptions] = useState<Array<{ value: string; label: string }>>([]);

  const { data: stockData } = useGetProductStockAtRackQuery(
    { productId: line.productId, rackId: line.rackId || null },
    { skip: !line.productId || procType !== 'SALES' },
  );
  const availableQty = stockData?.getProductStockAtRack;

  useEffect(() => {
    if (!line.chamberId) {
      setFloorOptions([]);
      return;
    }
    let cancelled = false;
    void fetchFloors({ page: 1, pageLimit: 200, chamberId: line.chamberId })
      .unwrap()
      .then((res) => {
        if (cancelled) return;
        setFloorOptions(
          (res.listFloors.rows ?? []).map((r) => ({ value: r.id, label: r.name })),
        );
      })
      .catch(() => {
        if (!cancelled) setFloorOptions([]);
      });
    return () => {
      cancelled = true;
    };
    // fetchFloors is a stable lazy trigger
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line.chamberId]);

  useEffect(() => {
    if (!line.floorId) {
      setRackOptions([]);
      return;
    }
    let cancelled = false;
    void fetchRacks({ page: 1, pageLimit: 200, floorId: line.floorId })
      .unwrap()
      .then((res) => {
        if (cancelled) return;
        setRackOptions(
          (res.listRacks.rows ?? []).map((r) => ({ value: r.id, label: r.name })),
        );
      })
      .catch(() => {
        if (!cancelled) setRackOptions([]);
      });
    return () => {
      cancelled = true;
    };
    // fetchRacks is a stable lazy trigger
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line.floorId]);

  function patchSub(subIndex: number, qtyBase: number) {
    const next = line.subs.map((s, i) =>
      i === subIndex
        ? recomputeSubQty({ ...s, qtyBase }, line.addOnItem, line.addOnItemQty)
        : s,
    );
    onUpdate(index, { subs: next });
  }

  function patchAddOnQty(addOnItemQty: number) {
    const next = line.subs.map((s) =>
      recomputeSubQty(s, line.addOnItem, addOnItemQty),
    );
    onUpdate(index, { addOnItemQty, subs: next });
  }

  return (
    <Paper withBorder radius="md" p="sm">
      <Stack gap="xs">
        <Group justify="space-between" align="center" gap="xs">
          <Badge size="sm" variant="light" color="gray">
            #{index + 1}
          </Badge>
          {total > 1 && (
            <ActionIcon
              size="sm"
              color="red"
              variant="subtle"
              onClick={() => onRemove(index)}
            >
              <IconTrash size={14} />
            </ActionIcon>
          )}
        </Group>

        <AsyncSearchSelect
          label={t('orders.colProduct')}
          required
          placeholder={t('orders.searchProduct')}
          value={line.productId}
          valueLabel={line.productName}
          onChange={(id, name) => onSelectProduct(index, id, name)}
          fetchPage={fetchProducts}
          error={errors?.productId}
        />

        <Group gap="xs" grow>
          <FormField label={t('orders.chamber')}>
            <Select
              data={chamberOptions}
              value={line.chamberId || null}
              searchable
              clearable
              placeholder={t('orders.selectChamber')}
              onChange={(value) => {
                const opt = chamberOptions.find((o) => o.value === value);
                onUpdate(index, {
                  chamberId: value ?? '',
                  chamberName: opt?.label ?? '',
                  floorId: '',
                  floorName: '',
                  rackId: '',
                  rackName: '',
                });
              }}
            />
          </FormField>
          <FormField label={t('orders.floor')}>
            <Select
              data={floorOptions}
              value={line.floorId || null}
              searchable
              clearable
              disabled={!line.chamberId}
              placeholder={t('orders.selectFloor')}
              onChange={(value) => {
                const opt = floorOptions.find((o) => o.value === value);
                onUpdate(index, {
                  floorId: value ?? '',
                  floorName: opt?.label ?? '',
                  rackId: '',
                  rackName: '',
                });
              }}
            />
          </FormField>
          <FormField label={t('orders.rack')}>
            <Select
              data={rackOptions}
              value={line.rackId || null}
              searchable
              clearable
              disabled={!line.floorId}
              placeholder={t('orders.selectRack')}
              onChange={(value) => {
                const opt = rackOptions.find((o) => o.value === value);
                onUpdate(index, {
                  rackId: value ?? '',
                  rackName: opt?.label ?? '',
                });
              }}
            />
          </FormField>
        </Group>

        {procType === 'SALES' && line.productId ? (
          <Badge variant="light" color="teal" size="sm" w="fit-content">
            {t(line.rackId ? 'orders.availableAtRack' : 'orders.availableAllLocations', {
              qty: availableQty != null ? Number(availableQty) : '…',
            })}
            {line.qtyUnit ? ` ${line.qtyUnit}` : ''}
          </Badge>
        ) : null}

        <Group gap="xs" grow>
          <FormField label={`${t('orders.quantity')}${unit}`} required error={errors?.quantity}>
            <NumberInput
              min={0}
              decimalScale={3}
              value={line.quantity}
              onChange={(val: string | number) =>
                onUpdate(index, { quantity: Number(val) || 0 })
              }
            />
          </FormField>
          <FormField label={t('orders.pricePerUnit', { unit: line.qtyUnit || 'unit' })}>
            <NumberInput
              min={0}
              decimalScale={2}
              value={line.pricePerUnit}
              onChange={(val: string | number) =>
                onUpdate(index, { pricePerUnit: Number(val) || 0 })
              }
            />
          </FormField>
        </Group>

        <Group gap="xs" grow>
          <FormField label={t('orders.lossPercent')}>
            <NumberInput
              min={0}
              max={100}
              decimalScale={2}
              value={line.lossPercent}
              onChange={(val: string | number) =>
                onUpdate(index, { lossPercent: Number(val) || 0 })
              }
            />
          </FormField>
          <FormField label={t('orders.lossKg')}>
            <NumberInput
              min={0}
              decimalScale={3}
              value={line.lossKg}
              onChange={(val: string | number) =>
                onUpdate(index, { lossKg: Number(val) || 0 })
              }
            />
          </FormField>
        </Group>

        {line.addOnItem && (
          <FormField
            label={`${t('orders.addOnQty')}${line.addOnItemName ? ` (${line.addOnItemName})` : ''}`}
          >
            <NumberInput
              min={0}
              decimalScale={3}
              value={line.addOnItemQty}
              onChange={(val: string | number) =>
                patchAddOnQty(Number(val) || 0)
              }
            />
          </FormField>
        )}

        {line.showSubse ? (
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              {t('orders.subsequentDeductions')}
            </Text>
            <FormField label={`${t('orders.standardDeduction')}${unit}`}>
              <NumberInput
                min={0}
                decimalScale={3}
                value={line.subs[0]?.qtyBase || 0}
                onChange={(val: string | number) =>
                  patchSub(0, Number(val) || 0)
                }
                rightSection={
                  line.addOnItem ? (
                    <Text size="xs" c="dimmed" pr={6}>
                      = {line.subs[0]?.quantity || 0}
                    </Text>
                  ) : null
                }
              />
            </FormField>
            <FormField label={`${t('orders.subsequentUndersize')}${unit}`}>
              <NumberInput
                min={0}
                decimalScale={3}
                value={line.subs[1]?.qtyBase || 0}
                onChange={(val: string | number) =>
                  patchSub(1, Number(val) || 0)
                }
                rightSection={
                  line.addOnItem ? (
                    <Text size="xs" c="dimmed" pr={6}>
                      = {line.subs[1]?.quantity || 0}
                    </Text>
                  ) : null
                }
              />
            </FormField>
            <FormField label={`${t('orders.subsequentDamage')}${unit}`}>
              <NumberInput
                min={0}
                decimalScale={3}
                value={line.subs[2]?.qtyBase || 0}
                onChange={(val: string | number) =>
                  patchSub(2, Number(val) || 0)
                }
                rightSection={
                  line.addOnItem ? (
                    <Text size="xs" c="dimmed" pr={6}>
                      = {line.subs[2]?.quantity || 0}
                    </Text>
                  ) : null
                }
              />
            </FormField>
          </Stack>
        ) : null}

        <Group justify="space-between">
          <Text size="xs" c="dimmed">
            {t('orders.netQty')}{' '}
            <Text span fw={700} c="red">
              {net}
            </Text>
          </Text>
          <Text size="xs" c="dimmed">
            {t('orders.lineTotal')}{' '}
            <Text span fw={700} c="dark">
              {formatCurrency(price)}
            </Text>
          </Text>
        </Group>
      </Stack>
    </Paper>
  );
}

// ─── Step 1 ───────────────────────────────────────────────────────────────────

interface Step1Props {
  state: Step1State;
  onChange: (patch: Partial<Step1State>) => void;
  onNext: () => void;
  onCancel: () => void;
  errors: Record<string, string>;
}

function Step1Form({ state, onChange, onNext, onCancel, errors }: Step1Props) {
  const { t } = useTranslation();
  const [fetchCustomers] = useLazyListCustomersQuery();
  const [fetchVendors] = useLazyListVendorsQuery();
  const [fetchWarehouses] = useLazyListWarehousesQuery();
  const [fetchProducts] = useLazyListProductsQuery();
  const productMetaRef = useRef<Map<string, ProductMeta>>(new Map());
  const { data: chambersData } = useListChambersQuery({ page: 1, pageLimit: 200 });
  const chamberOptions = useMemo(
    () =>
      (chambersData?.listChambers.rows ?? []).map((c) => ({
        value: c.id,
        label: c.name,
      })),
    [chambersData],
  );

  const loadCustomers = useCallback(
    async (args: { search: string; page: number; pageLimit: number }) => {
      const res = await fetchCustomers(args).unwrap();
      return {
        rows: asPickerOptions(res.listCustomers?.rows),
        total: res.listCustomers?.total ?? 0,
      };
    },
    [fetchCustomers],
  );

  const loadVendors = useCallback(
    async (args: { search: string; page: number; pageLimit: number }) => {
      const res = await fetchVendors(args).unwrap();
      return {
        rows: asPickerOptions(res.listVendors?.rows),
        total: (res.listVendors as { total?: number })?.total ?? 0,
      };
    },
    [fetchVendors],
  );

  const loadWarehouses = useCallback(
    async (args: { search: string; page: number; pageLimit: number }) => {
      const res = await fetchWarehouses(args).unwrap();
      return {
        rows: asPickerOptions(res.listWarehouses?.rows),
        total: (res.listWarehouses as { total?: number })?.total ?? 0,
      };
    },
    [fetchWarehouses],
  );

  const loadProducts = useCallback(
    async (args: { search: string; page: number; pageLimit: number }) => {
      const res = await fetchProducts(args).unwrap();
      const rows = res.listProducts?.rows ?? [];
      for (const r of rows) {
        productMetaRef.current.set(String(r.id), {
          showSubse: Boolean(r.showSubse),
          addOnItem: Boolean(r.addOnItem),
          addOnItemName: r.addOnItemName ?? '',
          unit: r.unit ?? '',
          pricePerQuantity: Number(r.pricePerQuantity ?? 0),
        });
      }
      return {
        rows: asPickerOptions(rows),
        total: res.listProducts?.total ?? 0,
      };
    },
    [fetchProducts],
  );

  function addProduct() {
    onChange({ products: [...state.products, emptyLine()] });
  }

  function removeProduct(idx: number) {
    onChange({ products: state.products.filter((_, i) => i !== idx) });
  }

  function updateProduct(idx: number, patch: Partial<ProductLine>) {
    onChange({
      products: state.products.map((p, i) => (i === idx ? { ...p, ...patch } : p)),
    });
  }

  function selectProduct(idx: number, id: string, name: string) {
    const meta = productMetaRef.current.get(id);
    updateProduct(idx, {
      productId: id,
      productName: name,
      showSubse: Boolean(meta?.showSubse),
      addOnItem: Boolean(meta?.addOnItem),
      addOnItemName: meta?.addOnItemName ?? '',
      addOnItemQty: meta?.addOnItem ? 1 : 0,
      qtyUnit: meta?.unit ?? '',
      pricePerUnit: meta?.pricePerQuantity ?? 0,
      subs: emptySubs(),
    });
  }

  const baseTotal = state.products.reduce((s, p) => s + linePrice(p), 0);

  return (
    <Stack gap="md">
      <Paper
        withBorder
        radius="md"
        p="sm"
        style={{ background: 'var(--mantine-color-gray-0)' }}
      >
        <Stack gap="sm">
          <Group gap="xs" align="center">
            <IconShoppingCart size={16} color="var(--mantine-color-brand-6)" />
            <Text size="sm" fw={600}>
              {t('orders.orderType')}
            </Text>
          </Group>
          <Group gap="sm" grow>
            <FormField label={t('orders.colType')}>
              <TypeToggle<ProcType>
                value={state.procType}
                onChange={(v) => onChange({ procType: v })}
                options={[
                  { value: 'SALES', label: t('orders.sales') },
                  { value: 'PURCHASE', label: t('orders.purchase') },
                ]}
              />
            </FormField>
            <FormField label={t('orders.party')}>
              <TypeToggle<PartyType>
                value={state.partyType}
                onChange={(v) =>
                  onChange({ partyType: v, partyId: '', partyName: '' })
                }
                options={[
                  { value: 'customer', label: t('common.customer') },
                  { value: 'vendor', label: t('common.vendor') },
                ]}
              />
            </FormField>
          </Group>
        </Stack>
      </Paper>

      <Paper withBorder radius="md" p="sm">
        <Stack gap="sm">
          <Group gap="xs" align="center">
            <IconUser size={16} color="var(--mantine-color-brand-6)" />
            <Text size="sm" fw={600}>
              {state.partyType === 'customer' ? t('common.customer') : t('common.vendor')}
            </Text>
          </Group>
          <AsyncSearchSelect
            key={state.partyType}
            label={state.partyType === 'customer' ? t('common.customer') : t('common.vendor')}
            required
            placeholder={t('orders.searchParty', {
              partyType:
                state.partyType === 'customer' ? t('common.customer') : t('common.vendor'),
            })}
            value={state.partyId}
            valueLabel={state.partyName}
            onChange={(id, name) => onChange({ partyId: id, partyName: name })}
            fetchPage={state.partyType === 'customer' ? loadCustomers : loadVendors}
            error={errors.partyId}
          />
        </Stack>
      </Paper>

      <Paper withBorder radius="md" p="sm">
        <Stack gap="sm">
          <Group gap="xs" align="center">
            <IconBuildingWarehouse
              size={16}
              color="var(--mantine-color-brand-6)"
            />
            <Text size="sm" fw={600}>
              {t('orders.warehouse')}
            </Text>
          </Group>
          <AsyncSearchSelect
            label={t('orders.warehouse')}
            required
            placeholder={t('orders.searchWarehouse')}
            value={state.warehouseId}
            valueLabel={state.warehouseName}
            onChange={(id, name) =>
              onChange({ warehouseId: id, warehouseName: name })
            }
            fetchPage={loadWarehouses}
            error={errors.warehouseId}
          />
        </Stack>
      </Paper>

      <Paper withBorder radius="md" p="sm">
        <Stack gap="sm">
          <Group gap="xs" align="center" justify="space-between">
            <Group gap="xs" align="center">
              <IconShoppingCart
                size={16}
                color="var(--mantine-color-brand-6)"
              />
              <Text size="sm" fw={600}>
                {t('common.products')}
              </Text>
              <Badge size="sm" variant="light" color="brand">
                {state.products.length}
              </Badge>
            </Group>
            <Button
              size="xs"
              variant="light"
              leftSection={<IconPlus size={13} />}
              onClick={addProduct}
            >
              {t('common.add')}
            </Button>
          </Group>

          {errors.products ? (
            <Alert color="red" radius="md" py={6} px={10}>
              <Text size="xs">{errors.products}</Text>
            </Alert>
          ) : null}

          <Stack gap="sm">
            {state.products.map((line, idx) => (
              <ProductRow
                key={idx}
                line={line}
                index={idx}
                total={state.products.length}
                procType={state.procType}
                onUpdate={updateProduct}
                onRemove={removeProduct}
                onSelectProduct={selectProduct}
                fetchProducts={loadProducts}
                chamberOptions={chamberOptions}
                errors={{
                  productId: errors[`product_${idx}_id`],
                  quantity: errors[`product_${idx}_qty`],
                }}
              />
            ))}
          </Stack>

          {baseTotal > 0 && (
            <>
              <Divider />
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  {t('orders.productsSubtotal')}
                </Text>
                <Text size="sm" fw={700}>
                  {formatCurrency(baseTotal)}
                </Text>
              </Group>
            </>
          )}
        </Stack>
      </Paper>

      <Group justify="space-between">
        <Button variant="subtle" color="gray" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button rightSection={<IconArrowRight size={15} />} onClick={onNext}>
          {t('common.next')}
        </Button>
      </Group>
    </Stack>
  );
}

// ─── Step 2 ───────────────────────────────────────────────────────────────────

interface Step2Props {
  step1: Step1State;
  state: Step2State;
  onChange: (patch: Partial<Step2State>) => void;
  onBack: () => void;
  onSubmit: () => void;
  saving: boolean;
  error?: string;
}

function Step2Form({
  step1,
  state,
  onChange,
  onBack,
  onSubmit,
  saving,
  error,
}: Step2Props) {
  const { t } = useTranslation();
  const [fetchLabour] = useLazyListLabourQuery();
  const [fetchTransport] = useLazyListTransportQuery();

  const loadLabour = useCallback(
    async (args: { search: string; page: number; pageLimit: number }) => {
      const res = await fetchLabour(args).unwrap();
      return {
        rows: asPickerOptions(res.listLabour?.rows),
        total: (res.listLabour as { total?: number })?.total ?? 0,
      };
    },
    [fetchLabour],
  );

  const loadTransport = useCallback(
    async (args: { search: string; page: number; pageLimit: number }) => {
      const res = await fetchTransport(args).unwrap();
      return {
        rows: asPickerOptions(res.listTransport?.rows),
        total: (res.listTransport as { total?: number })?.total ?? 0,
      };
    },
    [fetchTransport],
  );

  const { base, discountVal, taxVal, chargesVal, final } = computeTotal(
    step1.products,
    state,
  );

  return (
    <Stack gap="md">
      <Paper withBorder radius="md" p="sm">
        <Stack gap="sm">
          <Group gap="xs" align="center">
            <IconTruck size={16} color="var(--mantine-color-brand-6)" />
            <Text size="sm" fw={600}>
              {t('orders.logistics')}
            </Text>
          </Group>
          <Group gap="sm" grow>
            <AsyncSearchSelect
              label={t('orders.labour')}
              placeholder={t('orders.searchLabour')}
              value={state.labourId}
              valueLabel={state.labourName}
              onChange={(id, name) =>
                onChange({ labourId: id, labourName: name })
              }
              fetchPage={loadLabour}
            />
            <AsyncSearchSelect
              label={t('orders.transport')}
              placeholder={t('orders.searchTransport')}
              value={state.transportId}
              valueLabel={state.transportName}
              onChange={(id, name) =>
                onChange({ transportId: id, transportName: name })
              }
              fetchPage={loadTransport}
            />
          </Group>
          <FormField label={t('orders.vehicleNumber')}>
            <TextInput
              placeholder={t('orders.vehiclePlaceholder')}
              value={state.vehicleNo}
              onChange={(e) => onChange({ vehicleNo: e.currentTarget.value })}
            />
          </FormField>
          <Group gap="sm" grow>
            <FormField label={t('orders.bagNumber')}>
              <TextInput
                placeholder={t('orders.bagNumberPlaceholder')}
                value={state.bagNumber}
                onChange={(e) => {
                  const bagNumber = e.currentTarget.value;
                  onChange({
                    bagNumber,
                    receiptNumber: composeReceipt(bagNumber, state.loadNumber, ''),
                  });
                }}
              />
            </FormField>
            <FormField label={t('orders.loadNumber')}>
              <TextInput
                placeholder={t('orders.loadNumberPlaceholder')}
                value={state.loadNumber}
                onChange={(e) => {
                  const loadNumber = e.currentTarget.value;
                  onChange({
                    loadNumber,
                    receiptNumber: composeReceipt(state.bagNumber, loadNumber, ''),
                  });
                }}
              />
            </FormField>
          </Group>
          <FormField label={t('orders.receiptNumber')}>
            <TextInput
              placeholder={t('orders.receiptPlaceholder')}
              value={state.receiptNumber}
              onChange={(e) => onChange({ receiptNumber: e.currentTarget.value })}
            />
          </FormField>
          {step1.procType === 'SALES' ? (
            <FormField label={t('orders.gatePassNumber')}>
              <TextInput
                placeholder={t('orders.gatePassPlaceholder')}
                value={state.gatePassNumber}
                onChange={(e) => onChange({ gatePassNumber: e.currentTarget.value })}
              />
            </FormField>
          ) : null}
        </Stack>
      </Paper>

      <Paper withBorder radius="md" p="sm">
        <Stack gap="sm">
          <Group gap="xs" align="center">
            <IconCoins size={16} color="var(--mantine-color-brand-6)" />
            <Text size="sm" fw={600}>
              {t('orders.additionalCharges')}
            </Text>
          </Group>
          <Group gap="sm" grow>
            <FormField label={t('orders.chargeLabel')}>
              <TextInput
                placeholder={t('orders.chargeLabelPlaceholder')}
                value={state.chargesType}
                onChange={(e) =>
                  onChange({ chargesType: e.currentTarget.value })
                }
              />
            </FormField>
            <FormField label={t('orders.amountRupee')}>
              <NumberInput
                min={0}
                decimalScale={2}
                value={state.chargesAmount}
                onChange={(val) =>
                  onChange({ chargesAmount: Number(val) || 0 })
                }
              />
            </FormField>
          </Group>
          <FormField label={t('orders.applyAs')}>
            <TypeToggle<'add' | 'sub'>
              value={state.chargesDirection}
              onChange={(v) => onChange({ chargesDirection: v })}
              options={[
                { value: 'add', label: t('orders.applyAsAdd') },
                { value: 'sub', label: t('orders.applyAsSubtract') },
              ]}
            />
          </FormField>
        </Stack>
      </Paper>

      <Paper withBorder radius="md" p="sm">
        <Stack gap="sm">
          <Text size="sm" fw={600}>
            {t('orders.discountAndTax')}
          </Text>
          <Group gap="sm" align="flex-start" grow>
            <Stack gap={4}>
              <FormField label={t('orders.discount')}>
                <NumberInput
                  min={0}
                  decimalScale={2}
                  value={state.discount}
                  onChange={(val) => onChange({ discount: Number(val) || 0 })}
                />
              </FormField>
              <TypeToggle<'PERCENT' | 'FLAT'>
                value={state.discountType}
                onChange={(v) => onChange({ discountType: v })}
                options={[
                  { value: 'PERCENT', label: t('orders.percent') },
                  { value: 'FLAT', label: t('orders.flat') },
                ]}
              />
            </Stack>
            <Stack gap={4}>
              <FormField label={t('orders.tax')}>
                <NumberInput
                  min={0}
                  decimalScale={2}
                  value={state.tax}
                  onChange={(val) => onChange({ tax: Number(val) || 0 })}
                />
              </FormField>
              <TypeToggle<'PERCENT' | 'FLAT'>
                value={state.taxType}
                onChange={(v) => onChange({ taxType: v })}
                options={[
                  { value: 'PERCENT', label: t('orders.percent') },
                  { value: 'FLAT', label: t('orders.flat') },
                ]}
              />
            </Stack>
          </Group>
        </Stack>
      </Paper>

      <FormField label={t('common.remarks')}>
        <Textarea
          placeholder={t('orders.remarksPlaceholder')}
          value={state.remarks}
          onChange={(e) => onChange({ remarks: e.currentTarget.value })}
          autosize
          minRows={2}
        />
      </FormField>

      <Paper
        withBorder
        radius="md"
        p="sm"
        style={{ background: 'var(--mantine-color-brand-0)' }}
      >
        <Stack gap={6}>
          <Text size="sm" fw={700} c="brand">
            {t('orders.orderSummary')}
          </Text>
          <Group justify="space-between">
            <Text size="xs" c="dimmed">
              {t('orders.productsSubtotal')}
            </Text>
            <Text size="xs" fw={500}>
              {formatCurrency(base)}
            </Text>
          </Group>
          {discountVal > 0 && (
            <Group justify="space-between">
              <Text size="xs" c="dimmed">
                {t('orders.discount')}{' '}
                {state.discountType === 'PERCENT'
                  ? `(${state.discount}%)`
                  : t('orders.flatSuffix')}
              </Text>
              <Text size="xs" fw={500} c="red">
                − {formatCurrency(discountVal)}
              </Text>
            </Group>
          )}
          {taxVal > 0 && (
            <Group justify="space-between">
              <Text size="xs" c="dimmed">
                {t('orders.tax')}{' '}
                {state.taxType === 'PERCENT' ? `(${state.tax}%)` : t('orders.flatSuffix')}
              </Text>
              <Text size="xs" fw={500} c="orange">
                + {formatCurrency(taxVal)}
              </Text>
            </Group>
          )}
          {state.chargesAmount > 0 && (
            <Group justify="space-between">
              <Text size="xs" c="dimmed">
                {state.chargesType || t('orders.charges')} (
                {state.chargesDirection === 'add' ? '+' : '−'})
              </Text>
              <Text
                size="xs"
                fw={500}
                c={state.chargesDirection === 'add' ? 'orange' : 'red'}
              >
                {state.chargesDirection === 'add' ? '+ ' : '− '}
                {formatCurrency(Math.abs(chargesVal))}
              </Text>
            </Group>
          )}
          <Divider />
          <Group justify="space-between">
            <Text size="sm" fw={700}>
              {t('orders.total')}
            </Text>
            <Text size="sm" fw={900} c="brand">
              {formatCurrency(final)}
            </Text>
          </Group>
        </Stack>
      </Paper>

      {error && (
        <Alert color="red" radius="md">
          <Text size="sm">{error}</Text>
        </Alert>
      )}

      <Group justify="space-between">
        <Button
          variant="subtle"
          color="gray"
          leftSection={<IconArrowLeft size={15} />}
          onClick={onBack}
          disabled={saving}
        >
          {t('common.back')}
        </Button>
        <Button
          leftSection={<IconCheck size={15} />}
          loading={saving}
          onClick={onSubmit}
          color="brand"
        >
          {t('orders.create')}
        </Button>
      </Group>
    </Stack>
  );
}

// ─── Wizard shell ─────────────────────────────────────────────────────────────

export interface CreateOrderWizardProps {
  opened: boolean;
  onClose: () => void;
  defaultProcType?: ProcType;
}

export function CreateOrderWizard({
  opened,
  onClose,
  defaultProcType = 'SALES',
}: CreateOrderWizardProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<1 | 2>(1);
  const [s1, setS1] = useState<Step1State>(() => defaultS1(defaultProcType));
  const [s2, setS2] = useState<Step2State>(defaultS2);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState('');

  const [addUpdateProcurement, { isLoading: saving }] =
    useAddUpdateProcurementMutation();

  function resetForm() {
    setStep(1);
    setS1(defaultS1(defaultProcType));
    setS2(defaultS2());
    setErrors({});
    setSubmitError('');
  }

  useEffect(() => {
    if (opened) resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when opened / default type changes
  }, [opened, defaultProcType]);

  function resetAndClose() {
    resetForm();
    onClose();
  }

  const handleS1Change = useCallback((patch: Partial<Step1State>) => {
    setS1((prev) => ({ ...prev, ...patch }));
  }, []);

  const handleS2Change = useCallback((patch: Partial<Step2State>) => {
    setS2((prev) => ({ ...prev, ...patch }));
  }, []);

  function validateStep1(): boolean {
    const errs: Record<string, string> = {};
    if (!s1.partyId) errs.partyId = t('orders.wizard.validation.party');
    if (!s1.warehouseId) errs.warehouseId = t('orders.wizard.validation.warehouse');

    const complete = s1.products.filter((p) => p.productId && p.quantity > 0);
    if (!complete.length) errs.products = t('orders.wizard.validation.products');

    s1.products.forEach((p, i) => {
      const started = Boolean(p.productId) || p.pricePerUnit > 0;
      if (!started) return;
      if (!p.productId) errs[`product_${i}_id`] = t('orders.selectProduct');
      if (!p.quantity || p.quantity <= 0) errs[`product_${i}_qty`] = t('orders.wizard.validation.quantity');
    });

    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function goToStep2() {
    if (validateStep1()) setStep(2);
  }

  async function handleSubmit() {
    setSubmitError('');
    const { discountVal, taxVal, chargesVal, final } = computeTotal(
      s1.products,
      s2,
    );
    const payload: Record<string, unknown> = {
      procType: s1.procType,
      warehouseId: Number(s1.warehouseId),
      procAmount: final,
      taxAmount: taxVal,
      discountAmount: discountVal,
      chargesAmount: Math.abs(chargesVal),
      remarks: s2.remarks || undefined,
      vehicleNo: s2.vehicleNo || undefined,
      receiptNumber: s2.receiptNumber || undefined,
      gatePassNumber: s1.procType === 'SALES' ? s2.gatePassNumber || undefined : undefined,
      loadNumber: s2.loadNumber || undefined,
      bagNumber: s2.bagNumber || undefined,
      products: s1.products
        .filter((p) => p.productId)
        .map((p) => {
          const net = lineNetQty(p);
          return {
            productId: Number(p.productId),
            quantity: p.quantity,
            pricePerUnit: p.pricePerUnit,
            netQuantity: net,
            qtyUnit: p.qtyUnit || undefined,
            addOnItemQty: p.addOnItem ? p.addOnItemQty : undefined,
            chamberId: p.chamberId ? Number(p.chamberId) : undefined,
            floorId: p.floorId ? Number(p.floorId) : undefined,
            rackId: p.rackId ? Number(p.rackId) : undefined,
            lossPercent: p.lossPercent || 0,
            lossKg: p.lossKg || 0,
            subs: p.showSubse
              ? p.subs
                  .filter((s) => (Number(s.quantity) || 0) > 0 || (Number(s.qtyBase) || 0) > 0)
                  .map((s) => ({
                    subType: s.subType,
                    quantity: Number(s.quantity) || 0,
                    qtyBase: Number(s.qtyBase) || 0,
                  }))
              : [],
          };
        }),
    };
    if (s1.partyType === 'customer') payload.customerId = Number(s1.partyId);
    else payload.vendorId = Number(s1.partyId);
    if (s2.labourId) payload.labourId = Number(s2.labourId);
    if (s2.transportId) payload.transportId = Number(s2.transportId);

    try {
      await addUpdateProcurement(payload).unwrap();
      resetAndClose();
    } catch (e: unknown) {
      const msg =
        (e as { data?: { message?: string }; message?: string })?.data
          ?.message ??
        (e as { message?: string })?.message ??
        t('orders.createFailed');
      setSubmitError(msg);
    }
  }

  const title =
    s1.procType === 'SALES'
      ? t('orders.wizard.titleSales', { step })
      : t('orders.wizard.titlePurchase', { step });

  return (
    <BottomDrawer opened={opened} onClose={resetAndClose} title={title} size="xl">
      <StepIndicator step={step} />

      {step === 1 ? (
        <Step1Form
          state={s1}
          onChange={handleS1Change}
          onNext={goToStep2}
          onCancel={resetAndClose}
          errors={errors}
        />
      ) : (
        <Step2Form
          step1={s1}
          state={s2}
          onChange={handleS2Change}
          onBack={() => setStep(1)}
          onSubmit={() => void handleSubmit()}
          saving={saving}
          error={submitError}
        />
      )}
    </BottomDrawer>
  );
}
